import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";

import AppHeader from "./components/AppHeader";
import SidebarDrawer from "./components/SidebarDrawer";
import MainContent from "./components/MainContent";
import CameraModal from "./components/CameraModel";
import { useCameraStatus } from "./components/context/CameraStatusContext";
import ThemeContext from "./theme/ThemeContext";
import { useAuth } from "./auth/AuthContext";
import LoginPage from "./page/LoginPage";
import axiosInstance from "./api/axiosInstance";

const convertApiData = (streamMap = {}, sitesArray = []) => {
  const finalResult = {};

  // Recursively collect all assets from hierarchy
  const collectAssets = (node) => {
    let assets = [];

    if (Array.isArray(node.assets)) {
      assets.push(...node.assets);
    }

    if (Array.isArray(node.children)) {
      node.children.forEach((child) => {
        assets.push(...collectAssets(child));
      });
    }

    return assets;
  };

  sitesArray.forEach((site) => {
    const siteId = String(site.id);

    finalResult[siteId] = {
      site_name: site.name,
      assets: {},
    };

    const allAssets = collectAssets(site);

    allAssets.forEach((asset) => {
      const assetId = String(asset.id);
      const streams = streamMap[assetId] || [];

      let cameras = [];

      if (streams.length > 0) {
        // Asset has stream(s)
        cameras = streams.map((stream) => ({
          camera_id: stream.cameraId,
          camera_name: stream.cameraName,
          stream_name: stream.cameraName,
          stream_key: stream.streamKey,
          url: `https://api-streaming.cognecto.com/hls/${stream.streamKey}.m3u8`,
          assetId,
        }));

        finalResult[siteId].assets[assetId] = {
          asset_name: asset.name,
          cameras,
        };
      }
    });
  });

  return finalResult;
};

// Helper: Converts IMEI to stream URL
function generateStreamUrl(assetId, streamMap) {
  const key = assetId.toString();

  if (streamMap[key] && streamMap[key].length > 0) {
    const streamKey = streamMap[key][0].streamKey;
    return `https://api-streaming.cognecto.com/hls/${streamKey}.m3u8`;
  }

  return "";
}

function Main() {
  const { user } = useAuth();
  const { cameraStatusMap } = useCameraStatus();
  const { darkMode } = useContext(ThemeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State management
  const [expandedSite, setExpandedSite] = useState(null);
  const [expandedAsset, setExpandedAsset] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [searchCamera, setSearchCamera] = useState("");
  const [cameras, setCameras] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [columnCount, setColumnCount] = useState(3);
  const [gridSize, setGridSize] = useState(0);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [cameraData, setCameraData] = useState(null);
  const [streamMap, setStreamMap] = useState({});

  useEffect(() => {
    const loadCameraData = async () => {
      try {
        let videoData = await axiosInstance.get(`/api/streaming/all-streams`);
        setStreamMap(videoData.data);

        const { data } = await axiosInstance.get(
          `/api/site/hierarchy?includeAssets=true`,
        );
        const formattedData = convertApiData(videoData.data, data);
        setCameraData(formattedData);
        setLoadingData(false);
      } catch (error) {
        console.error("Error loading camera data:", error);
        setLoadingData(false);
      }
    };
    loadCameraData();
  }, []);

  useEffect(() => {
    const handleShowAllCameras = () => {
      let allCameras = [];

      Object.entries(cameraData || {}).forEach(([siteId, site]) => {
        Object.entries(site.assets || {}).forEach(([assetId, asset]) => {
          if (Array.isArray(asset.cameras)) {
            allCameras = [...allCameras, ...asset.cameras];
          }
        });
      });

      setCameras(allCameras);
      setExpandedSite(null);
      setExpandedAsset(null);
    };
    handleShowAllCameras();
  }, [cameraData]);

  if (!user) {
    return (
      <LoginPage
        onLoginSuccess={() => {
          /* auth context will update user */
        }}
      />
    );
  }

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const fetchCameras = (siteKey, assetKey) => {
    setLoadingData(true);

    const Cameras = cameraData?.[siteKey]?.assets?.[assetKey]?.cameras;

    if (Cameras && Cameras.length > 0) {
      setCameras(Cameras);
    } else {
      console.warn("⚠️ No cameras found for this asset:", assetKey);
      setCameras([]);
    }
    setLoadingData(false);
  };

  const handleSiteClick = (cityId, site) => {
    setExpandedSite((prev) => (prev === cityId ? null : cityId));
    let cameraList = [];
    const assets = site?.assets || {};

    Object.values(assets).forEach((asset) => {
      const cameras = asset?.cameras || [];
      cameraList.push(
        ...cameras.map((cam) => ({
          camera_id: cam.camera_id,
          camera_name: cam.camera_name.toLowerCase(),
          url: cam.url,
        })),
      );
    });

    setCameras(cameraList);
    setGridSize(cameraList.length);
  };

  const handleShowAllCameras = () => {
    let allCameras = [];

    Object.entries(cameraData || {}).forEach(([siteId, site]) => {
      Object.entries(site.assets || {}).forEach(([assetId, asset]) => {
        if (Array.isArray(asset.cameras)) {
          allCameras = [...allCameras, ...asset.cameras];
        }
      });
    });

    setCameras(allCameras);
    setExpandedSite(null);
    setExpandedAsset(null);
  };

  const handleCameraClick = (camera) => {
    setSelectedCamera(camera);
    setModalOpen(true);
  };

  const drawerProps = {
    open,
    collapsed,
    isMobile,
    isDesktop,
    loading: loadingData,
    data: cameraData,
    expandedSite,
    expandedAsset,
    darkMode,
    theme,
    onDrawerToggle: handleDrawerToggle,
    onCollapseToggle: () => setCollapsed(!collapsed),
    onSiteClick: handleSiteClick,
    onShowAllCameras: handleShowAllCameras,
    onFetchCameras: fetchCameras,
    setExpandedAsset,
  };

  const headerProps = {
    darkMode,
    isMobile,
    open,
    columnCount,
    onDrawerToggle: handleDrawerToggle,
    onColumnCountChange: setColumnCount,
  };

  const mainContentProps = {
    loading: loadingData,
    selectedPlace,
    cameras,
    searchCamera,
    columnCount,
    gridSize,
    theme,
    onCameraClick: handleCameraClick,
  };

  return (
    <>
      <CssBaseline />
      <AppHeader {...headerProps} />

      <Box sx={{ display: "flex", position: "relative" }}>
        <SidebarDrawer {...drawerProps} />
        <MainContent {...mainContentProps} />

        <CameraModal
          open={modalOpen}
          onClose={setModalOpen}
          selectedCamera={selectedCamera}
        />
      </Box>
    </>
  );
}

export default Main;
